<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue'
import type { UploadFileInfo } from 'naive-ui'
import { NButton, NUpload, NUploadDragger, useMessage } from 'naive-ui'
import { AppSpin, SvgIcon } from '@/components/common'
import { t } from '@/locales'
import { renderColor } from '@/composables/useRenderColor'
import fileSvgIcon from '@/assets/file-icon/file_svg_icon.svg'

const props = defineProps({
  fileList: {
    type: Array as () => UploadFileInfo[],
    required: true,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxFile: {
    type: Number,
    required: false,
    default: undefined,
  },
  maxFileSize: {
    type: Number,
    required: false,
    default: undefined,
  },
  maxTotalFileSize: {
    type: Number,
    required: false,
    default: undefined,
  },
  accept: {
    type: String,
    required: false,
    default: undefined,
  },
  showFileList: {
    type: Boolean,
    required: false,
    default: false,
  },
  showUploadDragger: {
    type: Boolean,
    required: false,
    default: false,
  },
  showPreviewButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  showRemoveButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  alwaysShowRemoveButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  showDownloadButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  showLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  iconHeader: {
    type: String,
    required: false,
    default: undefined,
  },
})

const emit = defineEmits([
  'update:file-list', 'change', 'before-upload', 'download', 'preview', 'remove', 'fileClick',
])

const attrs = useAttrs()
const message = useMessage()

const acceptText = computed(() => {
  return props.accept?.split(',') || []
})
const isDragOver = ref<boolean>(false)

const onUpdate = (fileList: UploadFileInfo[]) => {
  emit('update:file-list', fileList)
}

const onChange = (options: { file: UploadFileInfo; fileList: Array<UploadFileInfo>; event?: Event }) => {
  emit('change', options)
}

const onBeforeUpload = (options: { file: UploadFileInfo; fileList: Array<UploadFileInfo> }) => {
  // Kiểm tra số lượng file
  if (props.maxFile && options.fileList.length >= props.maxFile) {
    message.error(t('upload.error.too_many', { max: props.maxFile }))
    options.file.status = 'error'
    return false
  }
  // Kiểm tra loại file
  if (props.accept) {
    const types = props.accept?.split(',').map(t => t.trim())
    const matched = types.some((type) => {
      if (type.includes('*')) {
        const baseType = type?.split('/*')[0]
        return options.file.type?.startsWith(baseType)
      }
      if (type.startsWith('.'))
        return options.file.name.toLowerCase().endsWith(type.toLowerCase())

      return options.file.type === type
    })
    if (!matched) {
      message.error(t('upload.error.invalid_type'))
      options.file.status = 'error'
      return false
    }
  }
  // Kiểm tra kích thước file
  if (props.maxFileSize && options.file.file && options.file.file.size > props.maxFileSize * 1024 * 1024) {
    message.error(t('upload.error.too_large', { size: props.maxFileSize || '' }))
    options.file.status = 'error'
    return false
  }
  options.file.status = 'finished'
  emit('before-upload', options)
  return true
}

const onDownload = (file: UploadFileInfo) => {
  emit('download', file)
}

const onPreview = (file: UploadFileInfo, detail: { event: MouseEvent }) => {
  emit('preview', file, detail)
}

const onRemove = (options: { file: UploadFileInfo; fileList: Array<UploadFileInfo>; index: number }) => {
  emit('remove', options)
}

const onFileClick = (file: UploadFileInfo) => {
  emit('fileClick', file.id || (file as any).upload_file_id || file.name)
}

const handleDragEnter = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  isDragOver.value = true
}

const handleDragLeave = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  const target = e.currentTarget as HTMLElement
  if (!target?.contains(e.relatedTarget as Node))
    isDragOver.value = false
}

const handleDragOver = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  isDragOver.value = false

  const files = Array.from(e.dataTransfer?.files || [])
  const file: UploadFileInfo
		= {
		  id: '',
		  batchId: null,
		  name: '',
		  status: 'pending',
		  percentage: 0,
		  file: null as any,
		  url: null,
		  type: null,
		  thumbnailUrl: null,
		  fullPath: null,
		}
  const fileList: Array<UploadFileInfo> = []
  if (files?.length > 0) {
    files.forEach((item, index) => {
      if (index > 0)
        fileList.push({ ...file })

      file.id = Date.now().toString()
      file.batchId = Date.now().toString()
      file.name = item.name
      file.type = item.type
      file.fullPath = `/${item.name}`
      file.file = item
      const options = {
        file,
        fileList,
      }
      onBeforeUpload(options)
    })
  }
}
</script>

<template>
  <div style="display: contents;">
    <AppSpin :show="showLoading && loading">
      <NUpload
        :file-list="fileList"
        :multiple="multiple"
        :max="maxFile"
        :accept="accept"
        :show-upload-dragger="showUploadDragger"
        v-bind="attrs"
        :show-file-list="false"
        :show-preview-button="false"
        :show-remove-button="false"
        :show-download-button="false"
        @update:file-list="onUpdate"
        @change="onChange"
        @before-upload="onBeforeUpload"
        @download="null"
        @preview="null"
        @remove="null"
        @drop="handleDrop"
        @dragover="handleDragOver"
        @dragenter="handleDragEnter"
        @dragleave="handleDragLeave"
      >
        <div v-if="showUploadDragger" class="w-full flex flex-col">
          <NUploadDragger v-if="showUploadDragger" class="mb-" style="border-radius: 20px !important">
            <div class="flex flex-col items-center justify-center space-y-2">
              <img :src="iconHeader || fileSvgIcon" alt="pdfIcon" class="w-18 h-18">
              <p class="text-lg font-semibold">
                {{ $t('upload.uploadYourFile') }}
              </p>
              <NButton type="primary" style="padding: 20px 50px; border-radius: 10px;">
                <SvgIcon icon="ion:cloud-upload-outline" size="20" class="mr-2" />
                {{ $t('upload.drag_instruction') }}
              </NButton>
            </div>
            <div class="space-y-2" @click="(e) => e.stopPropagation()">
              <div class="flex items-center gap-1 justify-center m-4">
                <div class="flex items-center gap-1">
                  <!-- <SvgIcon icon="ion:information-circle-outline" size="18" /> -->
                  <div class="font-medium">
                    {{ $t('upload.supported_format') }}:
                  </div>
                </div>
                <div v-if="acceptText?.length" class="text-xs space-x-2">
                  <span
                    v-for="(item, index) in acceptText"
                    :key="index"
                    class="font-medium px-[6px] py-[2px] text-primary  rounded"
                    :style="{
                      backgroundColor: renderColor(0.1),
                    }"
                  >
                    {{ item }}
                  </span>
                </div>
              </div>

              <p class="text-xs font-medium text-gray-400">
                {{ $t('upload.max_size', { size: maxFileSize ? `${maxFileSize} MB` : '' }) }}
              </p>
            </div>
          </NUploadDragger>
        </div>
        <slot v-else />
      </NUpload>
    </AppSpin>
    <div v-if="showFileList && fileList?.length" class="upload-file-list mt-2 space-y-2" @click="(e) => e.stopPropagation()">
      <div v-for="(item, index) in fileList" :key="index" class="upload-file group p-3 rounded-2xl  border border-gray-200 shadow bg-white dark:bg-gray-800 cursor-pointer" @click="onFileClick(item)">
        <div class="upload-file-info flex items-center justify-between py-[6px]">
          <div class="flex items-center gap-1">
            <div class="rotate-[225deg] opacity-30">
              <SvgIcon icon="ion:attach-outline" size="20" />
            </div>
            <div>{{ item.name }}</div>
          </div>
          <div v-if="showPreviewButton || showRemoveButton || showDownloadButton" :class="alwaysShowRemoveButton && showRemoveButton ? 'flex items-center gap-1' : 'hidden group-hover:flex items-center gap-1'">
            <NButton v-if="showPreviewButton && item.url" text @click="(e) => onPreview(item, { event: e })">
              <SvgIcon icon="ion:eye-outline" size="20" />
            </NButton>
            <NButton v-if="showRemoveButton" text @click="onRemove({ file: item, fileList, index })">
              <SvgIcon icon="ion:trash-outline" size="20" class="text-red-500" />
            </NButton>
            <NButton v-if="showDownloadButton" text @click="onDownload(item)">
              <SvgIcon icon="ion:download-outline" size="20" />
            </NButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.n-upload-trigger) {
  width: 100%;
}
:deep(.n-upload-dragger) {
  background-color: white !important;
  @apply dark:bg-gray-900 !important;
}
</style>
